import { defineStore } from 'pinia';
import type { ISelectOption } from '@coreInterfaces/interface';
import moment from 'moment';

export const useElementStore = defineStore('element-store', {
    state: () => {
        return {
            select: {} as {
                [key: string]: any;
            }
        };
    },

    getters: {
        getSelectOptByKey:
            (state) =>
            (key: string): Array<ISelectOption> | any[] => {
                if (key in state.select) {
                    return state.select[key];
                }

                return [];
            }
    },

    actions: {
        setDefaultSelectOpt(): void {
            this.setupCampaignPerformanceDateTypeOpt();
            this.setupCommissionColumnOpt();
            this.setupCommissionReportTypeOpt();
            this.setupCountryOpt();
            this.setupCountryCurrencyOpt();
            this.setupCurrencyOpt();
            this.setupDateFilterOpt();
            this.setupGeneralStatementOpt();
            this.setupGeneralStatusOpt();
            this.setupHttpMethodOpt();
            this.setupModuleTypeOpt();
            this.setupProductTypesOpt();
            this.setupRegistrationStatusOpt();
            this.setupReportTypeOpt();
            this.setupReportFTDTypeOpt();
            this.setupReportTransactionTypeOpt();
            this.setupReportWinloseTypeOpt();
            this.setupSystemActionOpt();
            this.setupTrackingPositionOpt();
            this.setupTrackingTypesOpt();
        },

        setSelectOpt(key: string, options: Array<ISelectOption>): void {
            // console.debug('select', this.select, key);
            const sorted = options?.sort(function (a: any, b: any) {
                if (a['code'] == b['code']) return 0;
                if (a['code'] == '' || a['code'] == '0') return 1;
                if (b['code'] == '' || b['code'] == '0') return 1;

                return a['name'].localeCompare(b['name']);
            });

            if (!(key in this.select)) {
                let newSelect = {
                    [key]: sorted
                };

                this.select = {
                    ...this.select,
                    ...newSelect
                };
            } else {
                this.select[key] = sorted;
            }
        },

        setupCampaignPerformanceDateTypeOpt(): void {
            this.setSelectOpt('campaign_performance_date_type', [
                { id: 0, code: 'D', name: 'Day' },
                { id: 1, code: 'M', name: 'Month' }
            ]);
        },

        setupCommissionColumnOpt(): void {
            this.setSelectOpt('commission_column', [
                // { id: 0, code: '', name: 'None' },
                { id: 1, code: 'winlose', name: 'Winlose' },
                { id: 2, code: 'realbet', name: 'Realbet' }
            ]);
        },

        setupCommissionReportTypeOpt(): void {
            this.setSelectOpt('commission_report_type', [
                { id: 0, code: 'commission', name: 'Commission' },
                { id: 1, code: 'summary', name: 'Summary' }
            ]);
        },

        setupCountryOpt(): void {
            this.setSelectOpt('countries', [
                { id: '-1', code: '', name: 'All' },
                { id: 1, code: 'MY', name: 'Malaysia' },
                { id: 2, code: 'SG', name: 'Singapore' },
                { id: 3, code: 'VN', name: 'Vietnam' },
                { id: 4, code: 'ID', name: 'Indonesia' },
                { id: 5, code: 'TH', name: 'Thailand' },
                { id: 6, code: 'NT', name: 'International' },
                { id: 7, code: 'MM', name: 'Myanmar' },
                { id: 8, code: 'AU', name: 'Australia' },
                { id: 9, code: 'HK', name: 'Hong Kong' },
                { id: 10, code: 'BR', name: 'Brazil' },
                { id: 11, code: 'TW', name: 'Taiwan' },
                { id: 12, code: 'BN', name: 'Brunei' },
                { id: 13, code: 'IN', name: 'India' },
                { id: 14, code: 'MX', name: 'Mexico' }
            ]);
        },

        setupCountryCurrencyOpt(): void {
            this.setSelectOpt('countryCurrency', [
                { id: 1, code: 'MY', name: 'MYR' },
                { id: 2, code: 'SG', name: 'SGD' },
                { id: 3, code: 'VN', name: 'VND' },
                { id: 4, code: 'ID', name: 'IDR' },
                { id: 5, code: 'TH', name: 'THB' },
                { id: 6, code: 'NT', name: 'USD' },
                { id: 7, code: 'MM', name: 'MMK' },
                { id: 8, code: 'AU', name: 'AUD' },
                { id: 9, code: 'HK', name: 'HKD' },
                { id: 10, code: 'BR', name: 'BRL' },
                { id: 11, code: 'TW', name: 'TWD' },
                { id: 12, code: 'BN', name: 'BND' },
                { id: 13, code: 'IN', name: 'INR' },
                { id: 14, code: 'MX', name: 'MXN' }
            ]);
        },

        setupCurrencyOpt(): void {
            this.setSelectOpt('currency', [
                { id: '-1', code: '', name: 'All' },
                { id: 1, code: 'MYR', name: 'MYR' },
                { id: 2, code: 'SGD', name: 'SGD' },
                { id: 3, code: 'VND', name: 'VND' },
                { id: 4, code: 'IDR', name: 'IDR' },
                { id: 5, code: 'THB', name: 'THB' },
                { id: 6, code: 'USD', name: 'USD' },
                { id: 7, code: 'MMK', name: 'MMK' },
                { id: 8, code: 'AUD', name: 'AUD' },
                { id: 9, code: 'HKD', name: 'HKD' },
                { id: 10, code: 'BRL', name: 'BRL' },
                { id: 11, code: 'TWD', name: 'TWD' },
                { id: 12, code: 'BND', name: 'BND' },
                { id: 13, code: 'INR', name: 'INR' },
                { id: 14, code: 'MXN', name: 'MXN' }
            ]);
        },

        setupDateFilterOpt(): void {
            const now = moment();
            const today = now.clone().format('YYYY-MM-DD HH:mm:ss');
            const yesterday = now.clone().subtract(1, 'days').endOf('day').format('YYYY-MM-DD HH:mm:ss');
            const past7 = now.clone().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss');
            const past30 = now.clone().subtract(30, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss');

            const weekStart = now.clone().startOf('isoWeek').format('YYYY-MM-DD HH:mm:ss');
            const weekEnd = now.clone().endOf('isoWeek').format('YYYY-MM-DD HH:mm:ss');

            const lastWeekStart = now.clone().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD HH:mm:ss');
            const lastWeekEnd = now.clone().subtract(1, 'weeks').endOf('isoWeek').format('YYYY-MM-DD HH:mm:ss');

            const monthStart = now.clone().startOf('month').format('YYYY-MM-DD HH:mm:ss');
            const monthEnd = now.clone().endOf('month').format('YYYY-MM-DD HH:mm:ss');

            const lastMonthStart = now.clone().subtract(1, 'months').startOf('month').format('YYYY-MM-DD HH:mm:ss');
            const lastMonthEnd = now.clone().subtract(1, 'months').endOf('month').format('YYYY-MM-DD HH:mm:ss');

            // console.log(now);
            // console.log(yesterday);
            // console.log('Last 7 Days', past7, ' - ', yesterday);
            // console.log('Last 30 Days', past30, ' - ', yesterday);
            // console.log('This Week', weekStart, ' - ', yesterday, ' | ', weekEnd);
            // console.log('Last Week', lastWeekStart, ' - ', lastWeekEnd);
            // console.log('This Month', monthStart, ' - ', yesterday, ' | ', monthEnd);
            // console.log('Last Month', lastMonthStart, ' - ', lastMonthEnd);

            this.setSelectOpt('datetime_range', [
                { id: 0, code: `${today} ~ ${today}`, name: 'Today' },
                { id: 1, code: `${past7} ~ ${yesterday}`, name: 'Last 7 Days' },
                { id: 2, code: `${past30} ~ ${yesterday}`, name: 'Last 30 Days' },
                { id: 3, code: `${weekStart} ~ ${weekEnd}`, name: 'This Week' },
                { id: 4, code: `${lastWeekStart} ~ ${lastWeekEnd}`, name: 'Last Week' },
                { id: 5, code: `${monthStart} ~ ${yesterday}`, name: 'This Month' },
                { id: 6, code: `${lastMonthStart} ~ ${lastMonthEnd}`, name: 'Last Month' }
            ]);
        },

        setupGeneralStatementOpt(): void {
            this.setSelectOpt('general_statement', [
                { id: '-1', code: '', name: 'ALL' },
                { id: 1, code: 'true', name: 'TRUE' },
                { id: 2, code: 'false', name: 'FALSE' }
            ]);
        },

        setupGeneralStatusOpt(): void {
            this.setSelectOpt('general_status', [
                { id: '-1', code: '', name: 'ALL' },
                { id: 0, code: 'INACTIVE', name: 'INACTIVE' },
                { id: 1, code: 'ACTIVE', name: 'ACTIVE' }
            ]);
        },

        setupHttpMethodOpt(): void {
            this.setSelectOpt('http_method', [
                { id: 0, code: 'GET', name: 'GET' },
                { id: 1, code: 'POST', name: 'POST' }
            ]);
        },

        setupModuleTypeOpt(): void {
            this.setSelectOpt('module_type', [
                { id: 1, code: 'admin', name: 'Admin' },
                { id: 2, code: 'affiliate', name: 'Affiliate' },
                { id: 3, code: 'site_group', name: 'Site Group' }
            ]);
        },

        setupProductTypesOpt(): void {
            this.setSelectOpt('product_type', [
                { id: 0, code: '0', name: 'All' },
                { id: 1, code: '2', name: 'Sports' },
                { id: 2, code: '4', name: 'Slots' },
                { id: 3, code: '5', name: 'Live Casino' },
                { id: 4, code: '6', name: 'Number Game' },
                { id: 5, code: '7', name: '4D' },
                { id: 6, code: '8', name: 'Keno' },
                { id: 7, code: '99', name: 'Virtual Sports' },
                { id: 8, code: '100', name: 'Others' },
                { id: 9, code: '10', name: 'Poker' },
                { id: 10, code: '15', name: 'P2P' },
                { id: 11, code: '11', name: 'E-Sports' },
                { id: 12, code: '16', name: 'Fish' },
                { id: 13, code: '17', name: 'Club Owner' },
                { id: 14, code: '18', name: 'Board Game' }
            ]);
        },

        setupRegistrationStatusOpt(): void {
            this.setSelectOpt('registration_status', [
                { id: '-1', code: '', name: 'ALL' },
                { id: 0, code: 'PENDING', name: 'PENDING' },
                { id: 1, code: 'REJECTED', name: 'REJECTED' },
                { id: 2, code: 'APPROVED', name: 'APPROVED' }
            ]);
        },

        setupReportTypeOpt(): void {
            this.setSelectOpt('report_types', [
                { id: 0, code: 'transaction', name: 'Transaction' },
                { id: 1, code: 'win_lose', name: 'Win Lose' },
                { id: 2, code: 'game_type', name: 'Game Type' },
                { id: 3, code: 'first_time_deposit', name: 'First Time Deposits' }
                // { id: 4, code: 'commission', name: 'Commission' },
            ]);
        },

        setupReportFTDTypeOpt(): void {
            this.setSelectOpt('ftd_report_type', [
                { id: 0, code: 'affiliate', name: 'Affiliate', permit: ['stg'] },
                { id: 1, code: 'user', name: 'User' }
            ]);
        },

        setupReportTransactionTypeOpt(): void {
            this.setSelectOpt('transaction_report_type', [
                { id: 0, code: 'affiliate', name: 'Affiliate', permit: ['stg'] },
                { id: 1, code: 'date', name: 'Date' },
                { id: 2, code: 'user', name: 'User' }
            ]);
        },

        setupReportWinloseTypeOpt(): void {
            this.setSelectOpt('winlose_report_type', [
                { id: 0, code: 'date', name: 'Date' },
                { id: 1, code: 'user', name: 'User' }
            ]);
        },

        setupSystemActionOpt(): void {
            this.setSelectOpt('system_action', [
                { id: 1, code: 'affiliate-register', name: 'Affiliate Register' },
                { id: 2, code: 'affiliate-resend', name: 'Affiliate Resend' },
                { id: 3, code: 'affiliate-user-disable-2fa', name: 'Affiliate User 2FA Disabled' },
                { id: 4, code: 'affiliate-user-password-reset', name: 'Affiliate User Password Reset' },
                { id: 5, code: 'affiliate-user-reactivate', name: 'Affiliate User Reactivate' },
                { id: 6, code: 'create', name: 'Create' },
                { id: 7, code: 'delete', name: 'Delete' },
                { id: 8, code: 'download', name: 'Download' },
                { id: 9, code: 'get-upline', name: 'Get Upline' },
                { id: 10, code: 'registration-action', name: 'Registration Action' },
                { id: 11, code: 'site-group-admin-disable-2fa', name: 'Site Group Admin 2FA Disabled' },
                { id: 12, code: 'status-update', name: 'Status Update' },
                { id: 13, code: 'update', name: 'Update' },
                { id: 14, code: 'update-currency-rate', name: 'Update Currency Rate' },
                { id: 15, code: 'upline-setup', name: 'Upline Setup' },
                { id: 16, code: 'upload-affiliate-agent', name: 'Upload Affiliate Agent' }
            ]);
        },

        setupTrackingPositionOpt(): void {
            this.setSelectOpt('tracking_position', [
                { id: 0, code: 'bhead', name: 'Before Head' },
                { id: 1, code: 'abody', name: 'After Body' },
                { id: 2, code: 'bcbody', name: 'Before Closing Body' },
                { id: 3, code: 'conversion', name: 'Conversion' }
            ]);
        },

        setupTrackingTypesOpt(): void {
            this.setSelectOpt('tracking_type', [
                { id: 0, code: 'GA4', name: 'GA4' },
                { id: 1, code: 'GTAG', name: 'GTAG' },
                { id: 2, code: 'GTM', name: 'GTM' },
                { id: 3, code: 'KWAI', name: 'KUAISHOU' },
                { id: 4, code: 'METAPIXEL', name: 'META Pixel' },
                { id: 5, code: 'OPERAPIXEL', name: 'OPERA Pixel' }
            ]);
        }
    }
});
